import React, { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

interface RouteStop {
  orderId: string;
  customerName: string;
  address: string;
  phone: string | null;
  email: string;
  eta: string;
  etaFormatted: string;
}

interface RouteData {
  route: RouteStop[];
  totalDistance: number;
  totalDuration: number;
}

export default function DriverRoute() {
  const { routeId } = useParams<{ routeId: string }>();
  const [routeData, setRouteData] = useState<RouteData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [completedStops, setCompletedStops] = useState<Set<string>>(new Set());
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [isDriving, setIsDriving] = useState(false);

  useEffect(() => {
    if (!routeId) {
      setError('No route ID provided');
      return;
    }

    // Load route data from localStorage
    const storedRoute = localStorage.getItem(routeId);
    if (!storedRoute) {
      setError('Route not found');
      return;
    }

    try {
      const parsedRoute = JSON.parse(storedRoute);
      logger.debug('Loaded route data:', parsedRoute);
      setRouteData(parsedRoute);
    } catch (err) {
      logger.error('Error parsing route data:', err);
      setError('Invalid route data');
    }
  }, [routeId]);

  const startDriving = () => {
    if (!routeData || routeData.route.length === 0) {
      toast.error('No stops available');
      return;
    }
    setIsDriving(true);
    navigateToStop(routeData.route[currentStopIndex].address);
  };

  const toggleStopCompletion = (stopId: string, index: number) => {
    setCompletedStops(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stopId)) {
        newSet.delete(stopId);
      } else {
        newSet.add(stopId);
        // Move to next stop if available
        if (index < (routeData?.route.length || 0) - 1) {
          setCurrentStopIndex(index + 1);
          // If driving, automatically navigate to next stop
          if (isDriving && routeData) {
            navigateToStop(routeData.route[index + 1].address);
          }
        } else {
          toast.success('All deliveries completed!');
          setIsDriving(false);
        }
      }
      return newSet;
    });
  };

  const navigateToStop = (address: string) => {
    // Open in Google Maps app or web
    const encodedAddress = encodeURIComponent(address);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}&travelmode=driving`, '_blank');
  };

  const callCustomer = (phone: string) => {
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    window.location.href = `tel:${phone}`;
  };

  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!routeData) return <div className="p-4">Loading route data...</div>;

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <div className="bg-indigo-600 text-white p-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Delivery Route</h1>
        <p className="text-sm mt-1">
          {completedStops.size} of {routeData.route.length} stops completed
        </p>
        <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
          <div>
            <p className="opacity-75">Total Distance</p>
            <p className="font-medium">{(routeData.totalDistance / 1000).toFixed(1)} km</p>
          </div>
          <div>
            <p className="opacity-75">Estimated Time</p>
            <p className="font-medium">{Math.floor(routeData.totalDuration / 60)} min</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-b-lg">
        {!isDriving ? (
          <>
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-3">Delivery Stops</h2>
              <div className="space-y-3">
                {routeData.route.map((stop, index) => (
                  <div
                    key={stop.orderId}
                    className="p-3 rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="flex items-center mb-2">
                      <div className="bg-indigo-100 text-indigo-800 w-6 h-6 rounded-full flex items-center justify-center text-sm mr-2">
                        {index + 1}
                      </div>
                      <p className="font-medium">{stop.customerName}</p>
                    </div>
                    <p className="text-sm text-gray-600">{stop.address}</p>
                    <p className="text-sm text-gray-500">ETA: {stop.etaFormatted}</p>
                    {stop.phone && (
                      <p className="text-sm text-gray-600 mt-1">
                        Phone: {stop.phone}
                      </p>
                    )}
                    {stop.email && (
                      <p className="text-sm text-gray-600">
                        Email: {stop.email}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t">
              <button
                onClick={startDriving}
                className="w-full py-3 bg-green-600 text-white rounded-md font-medium flex items-center justify-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Start Driving
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Current Stop */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-lg font-semibold">Current Stop</h2>
                <div className="bg-indigo-100 text-indigo-800 w-8 h-8 rounded-full flex items-center justify-center">
                  {currentStopIndex + 1}
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <p className="font-medium">{routeData.route[currentStopIndex].customerName}</p>
                <p className="text-gray-600">{routeData.route[currentStopIndex].address}</p>
                <p className="text-sm text-gray-500">ETA: {routeData.route[currentStopIndex].etaFormatted}</p>
                {routeData.route[currentStopIndex].phone && (
                  <p className="text-sm text-gray-600">
                    Phone: {routeData.route[currentStopIndex].phone}
                  </p>
                )}
                {routeData.route[currentStopIndex].email && (
                  <p className="text-sm text-gray-600">
                    Email: {routeData.route[currentStopIndex].email}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {routeData.route[currentStopIndex].phone && (
                  <button
                    onClick={() => callCustomer(routeData.route[currentStopIndex].phone!)}
                    className="flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                    Call Customer
                  </button>
                )}
                <button
                  onClick={() => navigateToStop(routeData.route[currentStopIndex].address)}
                  className="flex items-center justify-center px-4 py-2 bg-indigo-600 text-white rounded-md"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  Navigate
                </button>
              </div>

              <button
                onClick={() => toggleStopCompletion(routeData.route[currentStopIndex].orderId, currentStopIndex)}
                className={`w-full mt-3 py-2 rounded-md ${
                  completedStops.has(routeData.route[currentStopIndex].orderId)
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}
              >
                {completedStops.has(routeData.route[currentStopIndex].orderId) ? '✓ Completed' : 'Mark as Completed'}
              </button>
            </div>

            {/* Upcoming Stops */}
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-3">Upcoming Stops</h2>
              <div className="space-y-3">
                {routeData.route.slice(currentStopIndex + 1).map((stop, index) => (
                  <div
                    key={stop.orderId}
                    className="p-3 rounded-lg border border-gray-200 bg-white"
                  >
                    <div className="flex items-center mb-2">
                      <div className="bg-gray-100 text-gray-800 w-6 h-6 rounded-full flex items-center justify-center text-sm mr-2">
                        {currentStopIndex + index + 2}
                      </div>
                      <p className="font-medium">{stop.customerName}</p>
                    </div>
                    <p className="text-sm text-gray-600">{stop.address}</p>
                    <p className="text-sm text-gray-500">ETA: {stop.etaFormatted}</p>
                    {stop.phone && (
                      <p className="text-sm text-gray-600">
                        Phone: {stop.phone}
                      </p>
                    )}
                    {stop.email && (
                      <p className="text-sm text-gray-600">
                        Email: {stop.email}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Completed Stops */}
            {completedStops.size > 0 && (
              <div className="p-4 border-t">
                <h2 className="text-lg font-semibold mb-3">Completed Stops</h2>
                <div className="space-y-3">
                  {routeData.route
                    .filter(stop => completedStops.has(stop.orderId))
                    .map(stop => (
                      <div key={stop.orderId} className="p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center mb-2">
                          <svg className="h-5 w-5 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="font-medium">{stop.customerName}</p>
                        </div>
                        <p className="text-sm text-gray-600">{stop.address}</p>
                        {stop.phone && (
                          <p className="text-sm text-gray-600">
                            Phone: {stop.phone}
                          </p>
                        )}
                        {stop.email && (
                          <p className="text-sm text-gray-600">
                            Email: {stop.email}
                          </p>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
} 